import { useCallback, useEffect, useState } from 'react';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import { IFilterChip } from '../../../Components/CoreLib/library';
import { JobDto } from '../../../dtos';
import { useLazyGetJobByIdQuery } from '../../../store/generated/generatedApi';
import { formatDate } from '../../../util';

// A malformed date in the URL (e.g. ?quoteDateAfter=abc) is ignored rather than becoming an Invalid Date that crashes the page
const parseDateParam = (value: string | null) => {
    if (!value) {
        return undefined;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

export interface IQuoteFilterOptions {
    job?: JobDto;
    quoteDateBefore?: Date;
    quoteDateAfter?: Date;
    effectiveDateBefore?: Date;
    effectiveDateAfter?: Date;
    expirationDateBefore?: Date;
    expirationDateAfter?: Date;
    showHistorical?: boolean;
}

export const CHIP_KEYS = {
    job: 'Job',
    quoteDateBefore: 'Quote Date Before',
    quoteDateAfter: 'Quote Date After',
    effectiveOnBefore: 'Effective Date Before',
    effectiveOnAfter: 'Effective Date After',
    expirationDateBefore: 'Expiration Date Before',
    expirationDateAfter: 'Expiration Date After',
    showHistorical: 'Show Historical'
};

export function useQuoteFilters() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [getJob, { data: loadedJob, isLoading: isLoadingJob }] = useLazyGetJobByIdQuery();

    // Read initial filter state from URL params
    const jobIdParam = searchParams.get('jobId');
    const quoteDateBeforeParam = searchParams.get('quoteDateBefore');
    const quoteDateAfterParam = searchParams.get('quoteDateAfter');
    const effectiveDateBeforeParam = searchParams.get('effectiveDateBefore');
    const effectiveDateAfterParam = searchParams.get('effectiveDateAfter');
    const expirationDateBeforeParam = searchParams.get('expirationDateBefore');
    const expirationDateAfterParam = searchParams.get('expirationDateAfter');
    const showHistoricalParam = searchParams.get('showHistorical');
    
    const [quoteFilters, setQuoteFilters] = useState<IQuoteFilterOptions>({
        job: jobIdParam ? { id: jobIdParam } as JobDto : undefined,
        quoteDateBefore: parseDateParam(quoteDateBeforeParam),
        quoteDateAfter: parseDateParam(quoteDateAfterParam),
        effectiveDateBefore: parseDateParam(effectiveDateBeforeParam),
        effectiveDateAfter: parseDateParam(effectiveDateAfterParam),
        expirationDateBefore: parseDateParam(expirationDateBeforeParam),
        expirationDateAfter: parseDateParam(expirationDateAfterParam),
        showHistorical: showHistoricalParam === 'true'
    });

    // Sync filter changes to URL params
    useEffect(() => {
        // Preserve other URL params (like sortKey, page, etc.)
        const currentParams = Object.fromEntries(searchParams.entries());
        const updatedParams: Record<string, string> = { ...currentParams };
        
        // Update or remove filter params
        if (quoteFilters.job?.id) {
            updatedParams['jobId'] = quoteFilters.job.id;
        } else {
            delete updatedParams['jobId'];
        }
        
        if (quoteFilters.quoteDateBefore) {
            updatedParams['quoteDateBefore'] = quoteFilters.quoteDateBefore.toISOString();
        } else {
            delete updatedParams['quoteDateBefore'];
        }
        
        if (quoteFilters.quoteDateAfter) {
            updatedParams['quoteDateAfter'] = quoteFilters.quoteDateAfter.toISOString();
        } else {
            delete updatedParams['quoteDateAfter'];
        }
        
        if (quoteFilters.effectiveDateBefore) {
            updatedParams['effectiveDateBefore'] = quoteFilters.effectiveDateBefore.toISOString();
        } else {
            delete updatedParams['effectiveDateBefore'];
        }
        
        if (quoteFilters.effectiveDateAfter) {
            updatedParams['effectiveDateAfter'] = quoteFilters.effectiveDateAfter.toISOString();
        } else {
            delete updatedParams['effectiveDateAfter'];
        }
        
        if (quoteFilters.expirationDateBefore) {
            updatedParams['expirationDateBefore'] = quoteFilters.expirationDateBefore.toISOString();
        } else {
            delete updatedParams['expirationDateBefore'];
        }
        
        if (quoteFilters.expirationDateAfter) {
            updatedParams['expirationDateAfter'] = quoteFilters.expirationDateAfter.toISOString();
        } else {
            delete updatedParams['expirationDateAfter'];
        }
        
        if (quoteFilters.showHistorical) {
            updatedParams['showHistorical'] = 'true';
        } else {
            delete updatedParams['showHistorical'];
        }

        setSearchParams(createSearchParams(updatedParams));
    }, [quoteFilters, searchParams, setSearchParams]);

    const handleSearchTermFilterChange = (updatedFilters: IQuoteFilterOptions) => {
        setQuoteFilters(updatedFilters);
    };

    useEffect(() => {
        if (quoteFilters.job?.id && (!loadedJob || loadedJob?.id !== quoteFilters.job?.id)) {
            getJob({ id: quoteFilters.job.id! }).then((result) => {
                setQuoteFilters({ ...quoteFilters, job: result.data });
            });
        }
    }, [quoteFilters.job, getJob, quoteFilters, loadedJob]);

    const getFilterChips = useCallback(() => {
        const filterChips: IFilterChip[] = [];
        if (!!quoteFilters.job) {
            filterChips.push({
                key: CHIP_KEYS.job,
                text: isLoadingJob ? 'Loading...' : quoteFilters.job?.jobNumber?.toString() ?? '',
            });
        }
        if (!!quoteFilters.quoteDateAfter) {
            filterChips.push({
                key: CHIP_KEYS.quoteDateAfter,
                text: formatDate(quoteFilters.quoteDateAfter),
            });
        }
        if (!!quoteFilters.quoteDateBefore) {
            filterChips.push({
                key: CHIP_KEYS.quoteDateBefore,
                text: formatDate(quoteFilters.quoteDateBefore),
            });
        }
        if (!!quoteFilters.effectiveDateAfter) {
            filterChips.push({
                key: CHIP_KEYS.effectiveOnAfter,
                text: formatDate(quoteFilters.effectiveDateAfter),
            });
        }
        if (!!quoteFilters.effectiveDateBefore) {
            filterChips.push({
                key: CHIP_KEYS.effectiveOnBefore,
                text: formatDate(quoteFilters.effectiveDateBefore),
            });
        }
        if (!!quoteFilters.expirationDateAfter) {
            filterChips.push({
                key: CHIP_KEYS.expirationDateAfter,
                text: formatDate(quoteFilters.expirationDateAfter),
            });
        }
        if (!!quoteFilters.expirationDateBefore) {
            filterChips.push({
                key: CHIP_KEYS.expirationDateBefore,
                text: formatDate(quoteFilters.expirationDateBefore),
            });
        }
        return filterChips;
    }, [quoteFilters, isLoadingJob]);

    const handleRemoveFilterChip = useCallback(
        (filterChipKey: string) => {
            switch (filterChipKey) {
                case CHIP_KEYS.job:
                    setQuoteFilters({
                        ...quoteFilters,
                        job: undefined,
                    });
                    break;
                case CHIP_KEYS.quoteDateBefore:
                    setQuoteFilters({
                        ...quoteFilters,
                        quoteDateBefore: undefined,
                    });
                    break;
                case CHIP_KEYS.quoteDateAfter:
                    setQuoteFilters({
                        ...quoteFilters,
                        quoteDateAfter: undefined,
                    });
                    break;
                case CHIP_KEYS.effectiveOnBefore:
                    setQuoteFilters({
                        ...quoteFilters,
                        effectiveDateBefore: undefined,
                    });
                    break;
                case CHIP_KEYS.effectiveOnAfter:
                    setQuoteFilters({
                        ...quoteFilters,
                        effectiveDateAfter: undefined,
                    });
                    break;
                case CHIP_KEYS.expirationDateBefore:
                    setQuoteFilters({
                        ...quoteFilters,
                        expirationDateBefore: undefined,
                    });
                    break;
                case CHIP_KEYS.expirationDateAfter:
                    setQuoteFilters({
                        ...quoteFilters,
                        expirationDateAfter: undefined,
                    });
                    break;
                default:
                    console.warn('Invalid filter chip key provided: ', filterChipKey);
                    break;
            }
        },
        [quoteFilters]
    );

    return {
        quoteFilters,
        handleSearchTermFilterChange,
        getFilterChips,
        handleRemoveFilterChip,
    };
}
